import { useEffect, useMemo, useRef } from 'react';
import useVisibilityAnalysisProgress from './useVisibilityAnalysisProgress';
import { useLazyFetchDomainVisibilityAnalysisQuery } from '../../service';
import { DomainVisibilityAnalysis } from '../../types';


type DomainVisibilityAnalysisHook = {
    data: DomainVisibilityAnalysis | undefined;
    isInProgress: boolean;
    isLoading: boolean;
    isFetchingLatestAnalysis: boolean;
    error: unknown;
};

/**
 * Orchestrates domain visibility analysis lifecycle:
 * - If an analysis process is active, we let the progress hook poll and expose loading
 * - If no active process, that means at least one analysis has completed. In this case, we are fetching the latest analysis
 */
export default function useDomainVisibilityAnalysis(domain: string): DomainVisibilityAnalysisHook {
    const { hasActiveProcess, isLoading: isLoadingActiveProcess } = useVisibilityAnalysisProgress(domain);
    const [fetchLatestAnalysis, fetchLatestAnalysisState] = useLazyFetchDomainVisibilityAnalysisQuery();

    // Prevent duplicate orchestration while conditions remain stable
    const attemptedRef = useRef(false);
    const inFlightFetchRef = useRef<ReturnType<typeof fetchLatestAnalysis> | null>(null);
    const isUnmountedRef = useRef(false);

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        // Only orchestrate when not actively analyzing and not loading the active-process status
        if (hasActiveProcess || isLoadingActiveProcess) return;

        // Avoid repeated triggers while state is stable
        if (attemptedRef.current) return;
        if (fetchLatestAnalysisState.isFetching) return;

        attemptedRef.current = true;
        const latestAnalysisPromise = fetchLatestAnalysis({ domain });
        inFlightFetchRef.current = latestAnalysisPromise;

        latestAnalysisPromise.unwrap()
            .catch(() => {
                return Promise.resolve();
            })
            .finally(() => {
                inFlightFetchRef.current = null;
            });

        // Cleanup: abort in-flight fetch if unmounting
        return () => {
            if (isUnmountedRef.current) {
                try {
                    inFlightFetchRef.current?.abort?.();
                } catch (_) { }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, hasActiveProcess, isLoadingActiveProcess, fetchLatestAnalysis]);

    // Reset the attempted flag when conditions change (e.g., active process starts/stops)
    useEffect(() => {
        if (hasActiveProcess || isLoadingActiveProcess) {
            attemptedRef.current = false;
        }
    }, [hasActiveProcess, isLoadingActiveProcess]);

    const isFetchingLatestAnalysis = !!fetchLatestAnalysisState.isFetching;
    const isLoading = !!(isLoadingActiveProcess || isFetchingLatestAnalysis);

    const non404Error = useMemo(() => {
        const e: any = fetchLatestAnalysisState.error;
        if (!e) return undefined;
        const status = (e && typeof e === 'object' && 'status' in e) ? e.status : e?.originalStatus;
        return status === 404 ? undefined : e;
    }, [fetchLatestAnalysisState.error]);

    return {
        data: fetchLatestAnalysisState.data,
        isInProgress: hasActiveProcess,
        isLoading,
        isFetchingLatestAnalysis,
        error: non404Error
    };
}
